import React from 'react';
import { type CallingCountryCode } from '@wix/form-validator';
import {
  Input as AriaInput,
  Select as AriaSelect,
  Button as AriaSelectButton,
  SelectValue as AriaSelectValue,
  Popover as AriaPopover,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
} from 'react-aria-components';

import { useFieldProps } from '../../contexts/field-props-context';
import { Field } from '../../headless/field';
import { type PublicPhoneInputProps } from '../../types/public-props';

import {
  getCallingCode,
  toLongCountryCode,
} from './phone-input/phone-input-utils';
import {
  usePhoneFieldContext,
  type PhoneFieldContextValue,
  PhoneFieldContext,
} from './phone-input-field-headless-context';
import { usePhoneInputLogic } from './phone-input/use-phone-input-logic';
import { RequiredIndicatorPlacement } from '@wix/ambassador-forms-v4-form/types';

export interface PhoneFieldProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const Root = React.forwardRef<HTMLElement, PhoneFieldProps>((props, ref) => {
  const { children, asChild, className } = props;
  const { id, value, defaultCountryCode, onChange, countryCodes } =
    useFieldProps<PublicPhoneInputProps>();

  const {
    currentValue,
    selectedCountryCode,
    handlePhoneChange,
    handleCountryCodeChange,
  } = usePhoneInputLogic({
    value,
    defaultCountryCode,
    allowedCountryCodes: countryCodes,
    onChange,
  });

  const contextValue: PhoneFieldContextValue = {
    currentValue,
    selectedCountryCode,
    handlePhoneChange,
    handleCountryCodeChange,
  };

  return (
    <PhoneFieldContext.Provider value={contextValue}>
      <Field ref={ref} id={id} asChild={asChild} className={className}>
        <Field.InputWrapper>
          <Field.Input>{children}</Field.Input>
        </Field.InputWrapper>
      </Field>
    </PhoneFieldContext.Provider>
  );
});

Root.displayName = 'PhoneField';

export interface PhoneFieldLabelProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const LabelRoot = React.forwardRef<HTMLElement, PhoneFieldLabelProps>(
  (props, ref) => {
    const { asChild, className, children } = props;
    const {
      id,
      label,
      showLabel,
      requiredIndicatorPlacement = RequiredIndicatorPlacement.AFTER_FIELD_TITLE,
    } = useFieldProps<PublicPhoneInputProps>();

    if (!showLabel) {
      return null;
    }

    let content: React.ReactNode;
    switch (requiredIndicatorPlacement) {
      case RequiredIndicatorPlacement.BEFORE_FIELD_TITLE:
        content = (
          <>
            {children}
            {label}
          </>
        );
        break;
      case RequiredIndicatorPlacement.AFTER_FIELD_TITLE:
      default:
        content = (
          <>
            {label}
            {children}
          </>
        );
    }

    return (
      <Field.Label ref={ref} asChild={asChild} className={className}>
        <label htmlFor={id}>{content}</label>
      </Field.Label>
    );
  },
);

LabelRoot.displayName = 'PhoneField.Label';

export interface PhoneFieldLabelRequiredProps {
  children?: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

export const Required = React.forwardRef<
  HTMLSpanElement,
  PhoneFieldLabelRequiredProps
>((props, ref) => {
  const { required, requiredIndicatorType } =
    useFieldProps<PublicPhoneInputProps>();

  return (
    <Field.Label.Required
      {...props}
      ref={ref}
      required={required}
      requiredIndicatorType={requiredIndicatorType}
    />
  );
});

Required.displayName = 'PhoneField.Label.Required';

export interface PhoneFieldLabelComponent
  extends React.ForwardRefExoticComponent<
    PhoneFieldLabelProps & React.RefAttributes<HTMLElement>
  > {
  Required: typeof Required;
}

export const Label = LabelRoot as PhoneFieldLabelComponent;
Label.Required = Required;

export interface PhoneFieldErrorProps {
  children?: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const Error = React.forwardRef<HTMLElement, PhoneFieldErrorProps>(
  (props, ref) => {
    const { children, ...rest } = props;
    const { errorMessage } = useFieldProps<PublicPhoneInputProps>();

    return (
      <Field.Error ref={ref} {...rest}>
        {errorMessage}
      </Field.Error>
    );
  },
);

Error.displayName = 'PhoneField.Error';

export interface PhoneFieldDescriptionProps {
  className?: string;
}

const Description = React.forwardRef<HTMLElement, PhoneFieldDescriptionProps>(
  ({ className }, ref) => {
    const { description } = useFieldProps<PublicPhoneInputProps>();

    if (!description) {
      return null;
    }

    return (
      <Field.Description
        ref={ref}
        className={className}
        description={description}
      />
    );
  },
);

Description.displayName = 'PhoneField.Description';

export interface PhoneFieldCountrySelectProps {
  className?: string;
  children?: React.ReactNode;
  placeholder?: string;
}

const CountrySelectRoot = React.forwardRef<
  HTMLElement,
  PhoneFieldCountrySelectProps
>((props, ref) => {
  const { className, children, placeholder, ...rest } = props;
  const { readOnly, onBlur, onFocus } = useFieldProps<PublicPhoneInputProps>();
  const { selectedCountryCode, handleCountryCodeChange } =
    usePhoneFieldContext();

  return (
    <Field.Input ref={ref} asChild className={className} {...rest}>
      <AriaSelect
        selectedKey={selectedCountryCode || ''}
        onSelectionChange={(key) =>
          handleCountryCodeChange(String(key) as CallingCountryCode)
        }
        isDisabled={readOnly}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
      >
        {children}
      </AriaSelect>
    </Field.Input>
  );
});

CountrySelectRoot.displayName = 'PhoneField.CountrySelect';

export interface PhoneFieldCountrySelectButtonProps {
  children?: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  PhoneFieldCountrySelectButtonProps
>(({ children, className }, ref) => {
  const { id } = useFieldProps<PublicPhoneInputProps>();

  return (
    <AriaSelectButton ref={ref} id={`${id}-country`} className={className}>
      {children}
    </AriaSelectButton>
  );
});

Button.displayName = 'PhoneField.CountrySelect.Button';

export interface PhoneFieldCountrySelectSelectedValueProps {
  className?: string;
}

const SelectedValue = React.forwardRef<
  HTMLButtonElement,
  PhoneFieldCountrySelectSelectedValueProps
>(({ className }, ref) => {
  return <AriaSelectValue ref={ref} className={className} />;
});

SelectedValue.displayName = 'PhoneField.CountrySelect.SelectedValue';

export interface PhoneFieldCountrySelectOptionsProps {
  children?: React.ReactNode;
  className?: string;
  renderOption: (data: {
    countryCode: CallingCountryCode;
    dialingCode: string;
  }) => React.ReactNode;
}

const Options = React.forwardRef<
  HTMLOptionElement,
  PhoneFieldCountrySelectOptionsProps
>(({ className, renderOption }) => {
  const { countryCodes } = useFieldProps<PublicPhoneInputProps>();
  return (
    <AriaPopover>
      <AriaListBox className={className}>
        {countryCodes?.map((countryCode) => {
          const longCountryCode = toLongCountryCode(countryCode);
          const dialingCode = getCallingCode(longCountryCode) || '';
          const optionElement = renderOption({ countryCode, dialingCode });

          return React.cloneElement(optionElement as React.ReactElement, {
            key: countryCode,
            value: countryCode,
          });
        })}
      </AriaListBox>
    </AriaPopover>
  );
});

Options.displayName = 'PhoneField.CountrySelect.Options';

export interface PhoneFieldCountrySelectOptionProps {
  /**
   * @internal - This prop is injected automatically. Do not use it.
   */
  value?: string;
  children?: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const Option = React.forwardRef<
  HTMLOptionElement,
  PhoneFieldCountrySelectOptionProps
>(({ className, value = '', children }) => {
  return (
    <AriaListBoxItem
      key={value}
      id={value}
      className={className}
      textValue={value}
    >
      {children}
    </AriaListBoxItem>
  );
});

Option.displayName = 'PhoneField.CountrySelect.Option';

export interface PhoneFieldCountrySelectComponent
  extends React.ForwardRefExoticComponent<
    PhoneFieldCountrySelectProps & React.RefAttributes<HTMLElement>
  > {
  Button: typeof Button;
  SelectedValue: typeof SelectedValue;
  Options: typeof Options;
  Option: typeof Option;
}

export const CountrySelect =
  CountrySelectRoot as PhoneFieldCountrySelectComponent;
CountrySelect.Button = Button;
CountrySelect.SelectedValue = SelectedValue;
CountrySelect.Options = Options;
CountrySelect.Option = Option;

export interface PhoneFieldInputProps {
  className?: string;
}

const Input = React.forwardRef<HTMLInputElement, PhoneFieldInputProps>(
  (props, ref) => {
    const { className } = props;
    const {
      id,
      required,
      readOnly,
      placeholder,
      onBlur,
      onFocus,
      errorMessage,
    } = useFieldProps<PublicPhoneInputProps>();
    const { currentValue, handlePhoneChange } = usePhoneFieldContext();

    return (
      <Field.Input asChild>
        <AriaInput
          type="tel"
          ref={ref}
          className={className}
          id={id}
          placeholder={placeholder}
          value={currentValue}
          onChange={(e) => handlePhoneChange(e.target.value)}
          required={required}
          readOnly={readOnly}
          disabled={readOnly}
          aria-invalid={!!errorMessage}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </Field.Input>
    );
  },
);

Input.displayName = 'PhoneField.Input';

export interface PhoneFieldComponent
  extends React.ForwardRefExoticComponent<
    PhoneFieldProps & React.RefAttributes<HTMLElement>
  > {
  Label: typeof Label;
  Error: typeof Error;
  Description: typeof Description;
  Input: typeof Input;
  CountrySelect: typeof CountrySelect;
}

export const PhoneField = Root as PhoneFieldComponent;
PhoneField.Label = Label;
PhoneField.Error = Error;
PhoneField.Description = Description;
PhoneField.Input = Input;
PhoneField.CountrySelect = CountrySelect;
